import { useMemo, useState } from 'react';

import * as Sentry from '@sentry/react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Button } from '@/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/shadcn/command';
import { cn } from '@/shadcn/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/popover';

import type { SelectOption } from '@/components/forms/components/form/writer/widgets/selectMenu/selectMenu.schema';
import {
  areOptionsArrayOfStrings,
  areOptionsCorrect
} from '@/components/forms/components/form/writer/widgets/selectMenu/selectMenu.utils';
import { isLockedWidget } from '@/components/forms/utils/links';
import { useDynamicProps } from '@/hooks/useDynamicProps';
import { usePropsSynchronization } from '@/hooks/usePropsSynchronization';
import type { WidgetWriterProps } from '../widget.type';
import { asNumber, guessType } from './utils';

const nums = new Set(['number', 'integer']);

type Schema = {
  title: string;
  type: string;
  items: { type: string };
  enum: any;
};

type Props = {
  schema: Schema;
  options: {
    enumOptions: SelectOption[];
  };
  /**
   * New field to avoid nesting options under enumOptions on the configuration side when declaring dynamic Props,
   * this will simplify the syntax.
   */
  enum?: SelectOption[];
  autofocus?: boolean;
} & WidgetWriterProps;

export const SelectMenu = (rjsfProps: Props) => {
  const props = useDynamicProps(rjsfProps);
  usePropsSynchronization(props, rjsfProps, v => handleChange(v));

  const [open, setOpen] = useState(false);

  const inputOptions = useMemo<SelectOption[]>(() => {
    const rawOptions = props.enum || (props.options.enumOptions as unknown);
    if (areOptionsCorrect(rawOptions)) {
      return rawOptions;
    }
    if (areOptionsArrayOfStrings(rawOptions)) {
      // If the options are an array of strings, map them to the expected format
      return rawOptions.map(value => ({
        label: value,
        value
      }));
    }

    const warnMsg =
      'SelectMenu: options are not in the expected format, expected an array of strings or an array of SelectOption objects.';

    console.warn(warnMsg);
    Sentry.captureMessage(warnMsg);
    return [];
  }, [props.options, props.enum]);

  const processValue = value => {
    if (value === undefined) return;

    // 'enum' is a reserved word, so only 'type' and 'items' can be destructured
    const { type, items } = props.schema;
    if (value === '') {
      return;
    }
    if (type === 'array' && items && nums.has(items.type)) {
      return value.map(asNumber);
    }
    if (type === 'boolean') {
      return value === 'true';
    }
    if (type === 'number') {
      return asNumber(value);
    }

    // If type is undefined, but an enum is present, try and infer the type from
    // the enum values
    if (props.schema.enum) {
      if (props.schema.enum.every(x => guessType(x) === 'number')) {
        return asNumber(value);
      }
      if (props.schema.enum.every(x => guessType(x) === 'boolean')) {
        return value === 'true';
      }
    }

    return value;
  };

  const handleChange = (newValue: string) => {
    props.onChange(processValue(newValue));
  };
  // check if the widget is locked because of forms links
  const isLocked = isLockedWidget(props.id, props.formContext?.links);

  const selectedValue = useMemo(
    () => inputOptions.find(option => option.value === props.value)?.label,
    [inputOptions, props.value]
  );

  const placeholder = props.placeholder || 'Select a value';
  const isAutofocused = props.autoFocus || props.uiSchema?.['ui:autofocus'];

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger
        asChild
        className="disabled:cursor-not-allowed"
        disabled={props.disabled || isLocked}
      >
        <Button
          aria-expanded={open}
          autoFocus={isAutofocused}
          className={cn(
            'h-[var(--radix-select-trigger-height)] w-full justify-between border-input font-normal text-foreground text-sm normal-case active:scale-100 disabled:cursor-not-allowed',
            !selectedValue && 'text-foreground/60',
            props.invalid && 'border-destructive'
          )}
          data-cy={`select-${props.name}`}
          disabled={props.disabled || isLocked}
          id={props.id}
          role="combobox"
          variant="outline"
        >
          <span className="truncate">{selectedValue || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command>
          {!props.uiSchema?.['ui:disabledSearch'] && (
            <CommandInput
              data-cy={`select-${props.name}-input`}
              placeholder="Search..."
            />
          )}

          <CommandList>
            <CommandEmpty>No items found.</CommandEmpty>
            <CommandGroup>
              {inputOptions.map(option => (
                <CommandItem
                  data-cy={`select-${props.name}-item-${option.value}`}
                  key={option.value}
                  onSelect={currentValue => {
                    handleChange(currentValue);
                    setOpen(false);
                  }}
                  value={option.value}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      props.value === option.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
